import { OrganizationProjection } from "@/api/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { MoreActions, OrganizationForm, PageHeader, Pill } from "./shared";

export function OrganizationsSection({
  organizations,
  form,
  activeTenantId,
  activeProgramCount,
  onFormChange,
  onCreate,
  onSelect,
  onDeactivate,
}: {
  organizations: OrganizationProjection[];
  form: OrganizationForm;
  activeTenantId: number | null;
  activeProgramCount: number;
  onFormChange: (form: OrganizationForm) => void;
  onCreate: () => void;
  onSelect: (organization: OrganizationProjection) => void;
  onDeactivate: (organization: OrganizationProjection) => void;
}) {
  return (
    <>
      <PageHeader
        title="Organizations"
        subtitle="Create and manage organizations running scholarship programs."
      />
      <div className="space-y-4 p-4">
        <form
          className="rounded-lg border bg-white p-4"
          onSubmit={(event) => {
            event.preventDefault();
            onCreate();
          }}
        >
          <label className="text-sm font-medium">
            Organization name
            <div className="mt-1 text-xs font-normal text-gray-500">
              Example: Saksham Foundation or Datamatics CSR.
            </div>
            <div className="mt-2 flex gap-2">
              <Input
                value={form.name}
                onChange={(event) => onFormChange({ ...form, name: event.target.value })}
                required
              />
              <Button type="submit">Create Organization</Button>
            </div>
          </label>
        </form>

        <div className="overflow-hidden rounded-lg border bg-white">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Applications</TableHead>
                  <TableHead>Active programs</TableHead>
                  <TableHead>Last activity</TableHead>
                  <TableHead>Primary admin</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {organizations.map((organization) => (
                  <TableRow key={organization.id}>
                    <TableCell>
                      <div className="font-medium">{organization.name}</div>
                      <div className="text-xs text-gray-500">{organization.slug}</div>
                    </TableCell>
                    <TableCell>
                      <Pill tone={organization.is_active ? "green" : "slate"}>
                        {organization.status}
                      </Pill>
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {organization.id === activeTenantId
                        ? "Available in program reports"
                        : "Open to view"}
                    </TableCell>
                    <TableCell className="font-medium">
                      {organization.id === activeTenantId ? activeProgramCount : "Open to view"}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {organization.recent_activity ?? "No recent activity"}
                    </TableCell>
                    <TableCell>{organization.primary_admin ?? "Not assigned"}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => onSelect(organization)}
                        >
                          Open
                        </Button>
                        <MoreActions>
                          <Button type="button" variant="outline" size="sm" disabled>
                            Assign admin
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => onDeactivate(organization)}
                            disabled={!organization.is_active}
                          >
                            Deactivate
                          </Button>
                        </MoreActions>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        <details className="rounded-lg border bg-white">
          <summary className="cursor-pointer px-4 py-3 text-sm font-medium">
            Deactivate selected organization
          </summary>
          <div className="space-y-3 border-t p-4">
            <p className="text-sm text-gray-600">
              Deactivation stops future use. Historical records remain available for audit and reporting.
            </p>
            <Textarea
              value={form.deactivate_reason}
              onChange={(event) =>
                onFormChange({ ...form, deactivate_reason: event.target.value })
              }
              placeholder="Reason for deactivation"
            />
          </div>
        </details>
      </div>
    </>
  );
}
